import type { SignalCategoryType } from '@terra/shared-schemas-orchestrator';
import {
  SignalCategorySchema,
  FindingSeveritySchema,
  FindingStatusSchema,
  type FindingStatusType,
} from '@terra/shared-schemas-orchestrator';
import {
  formatCategoryForDisplay,
  formatWordWithKnownAcronyms,
} from '@terra/shared-utils';
import type { BadgeSeverityType } from '../components/styled/badge-severity';

export { formatCategoryForDisplay };

export function getCategorySeverity(
  category?: SignalCategoryType | string | null,
): BadgeSeverityType {
  switch (category) {
    case SignalCategorySchema.enum.REFLECTED_XSS:
    case SignalCategorySchema.enum.STORED_XSS:
    case SignalCategorySchema.enum.DOM_XSS:
    case SignalCategorySchema.enum.SQL_INJECTION:
    case SignalCategorySchema.enum.CSRF:
    case SignalCategorySchema.enum.SSRF:
    case SignalCategorySchema.enum.BROKEN_ACCESS:
      return FindingSeveritySchema.enum.Critical;
    case SignalCategorySchema.enum.FILE_UPLOAD:
    case SignalCategorySchema.enum.INFORMATION_DISCLOSURE:
    case SignalCategorySchema.enum.CVE:
      return FindingSeveritySchema.enum.Medium;
    default:
      return FindingSeveritySchema.enum.Low;
  }
}

export function formatCategorySmart(
  category: SignalCategoryType | string,
): string {
  return formatCategoryForDisplay(category);
}

export function formatEnumForDisplay(value?: string | null): string {
  if (!value) return '';

  if (value.includes('_')) {
    return formatCategoryForDisplay(value);
  }

  const withSpaces = value.replace(/([a-z])([A-Z])/g, '$1 $2');

  return withSpaces
    .split(' ')
    .filter((word) => word.length > 0)
    .map(formatWordWithKnownAcronyms)
    .join(' ');
}

export function getCategoryLabel(category?: string): string {
  if (!category) return 'Unknown';

  return formatCategoryForDisplay(category);
}

export function getCategoryTranslationKey(
  category?: SignalCategoryType | string | null,
): string {
  if (!category) return 'unknown';

  switch (category) {
    case 'REFLECTED_XSS':
    case 'STORED_XSS':
    case 'DOM_XSS':
      return 'xss';
    case 'SQL_INJECTION':
      return 'sqlInjection';
    case 'CSRF':
      return 'csrf';
    case 'SSRF':
      return 'ssrf';
    case 'BROKEN_ACCESS':
      return 'brokenAccess';
    case 'FILE_UPLOAD':
      return 'fileUpload';
    case 'INFORMATION_DISCLOSURE':
      return 'informationDisclosure';
    case 'CVE':
      return 'cve';
    case 'OTHER':
      return 'other';
    default:
      return 'unknown';
  }
}

export function getFindingCategory(finding: {
  signal?: { category?: SignalCategoryType };
}): SignalCategoryType | undefined {
  return finding.signal?.category;
}

export function getCategoryColorScheme(
  category?: SignalCategoryType | string | null,
): string {
  if (!category) return 'gray';

  switch (category) {
    case 'REFLECTED_XSS':
    case 'STORED_XSS':
    case 'DOM_XSS':
    case 'SQL_INJECTION':
    case 'CVE':
      return 'red';
    case 'CSRF':
    case 'SSRF':
    case 'BROKEN_ACCESS':
      return 'orange';
    case 'INFORMATION_DISCLOSURE':
      return 'yellow';
    case 'FILE_UPLOAD':
      return 'blue';
    case 'OTHER':
    default:
      return 'gray';
  }
}

export function getCategorySeverityColor(category?: string): string {
  return getCategoryColorScheme(category);
}

export const FINDING_STATUS_DISPLAY_MAP: Record<FindingStatusType, string> = {
  Resolved: 'Resolved',
  Unresolved: 'Unresolved',
  InProgress: 'In Progress',
  FalsePositive: 'False Positive',
  WontFix: 'Accepted Risk',
  Retest: 'Retest',
};

export function formatFindingStatusForDisplay(
  status?: FindingStatusType | string | null,
): string {
  if (!status) return '';

  const parsed = FindingStatusSchema.safeParse(status);

  return parsed.success
    ? FINDING_STATUS_DISPLAY_MAP[parsed.data]
    : formatEnumForDisplay(status);
}
